import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
  Alert,
  createFilterOptions
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, Warning as WarningIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { warningsService } from '../../services/warnings.service';
import employeesService from '../../services/employees.service';
import { branchesService } from '../../services/branches.service';
import { CreateWarningDto, UpdateWarningDto, Warning } from '../../types/warnings.types';
import { Employee } from '../../types/employees.types';
import { Branch } from '../../types/branches.types';

interface WarningDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  warning?: Warning | null;
}

const filterOptions = createFilterOptions<Employee>({
  matchFrom: 'any',
  stringify: (option: Employee) => `${option.employeeCode} ${option.fullName} ${option.fullNameAr}`,
});

const WarningDialog: React.FC<WarningDialogProps> = ({ open, onClose, onSave, warning }) => {
  const isRTL = localStorage.getItem('language') === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    branchId: 0,
    employeeId: 0,
    warningType: 'Verbal',
    violationType: 'LateComing',
    violationDate: new Date().toISOString().split('T')[0],
    issueDate: new Date().toISOString().split('T')[0],
    description: '',
    actionTaken: '',
    penaltyAmount: 0,
    suspensionDays: 0,
    expiryDate: '',
    notes: ''
  });

  const t = {
    title: isRTL ? (warning ? 'تعديل إنذار' : 'إضافة إنذار') : (warning ? 'Edit Warning' : 'Add Warning'),
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    warningType: isRTL ? 'نوع الإنذار' : 'Warning Type',
    violationType: isRTL ? 'نوع المخالفة' : 'Violation Type',
    violationDate: isRTL ? 'تاريخ المخالفة' : 'Violation Date',
    issueDate: isRTL ? 'تاريخ الإصدار' : 'Issue Date',
    description: isRTL ? 'الوصف' : 'Description',
    actionTaken: isRTL ? 'الإجراء المتخذ' : 'Action Taken',
    penaltyAmount: isRTL ? 'مبلغ الجزاء' : 'Penalty Amount',
    suspensionDays: isRTL ? 'أيام الإيقاف' : 'Suspension Days',
    expiryDate: isRTL ? 'تاريخ الانتهاء' : 'Expiry Date',
    notes: isRTL ? 'ملاحظات' : 'Notes',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    selectBranch: isRTL ? 'اختر الفرع' : 'Select Branch',
    verbal: isRTL ? 'شفهي' : 'Verbal',
    firstWritten: isRTL ? 'كتابي أول' : 'First Written',
    secondWritten: isRTL ? 'كتابي ثاني' : 'Second Written',
    finalWritten: isRTL ? 'كتابي نهائي' : 'Final Written',
    financialPenalty: isRTL ? 'جزاء مالي' : 'Financial Penalty',
    suspension: isRTL ? 'إيقاف عن العمل' : 'Suspension',
    termination: isRTL ? 'فصل' : 'Termination',
    lateComing: isRTL ? 'تأخير' : 'Late Coming',
    absence: isRTL ? 'غياب' : 'Absence',
    performanceIssue: isRTL ? 'مشكلة أداء' : 'Performance Issue',
    misconduct: isRTL ? 'سوء سلوك' : 'Misconduct',
    policyViolation: isRTL ? 'مخالفة سياسة' : 'Policy Violation',
    safetyViolation: isRTL ? 'مخالفة سلامة' : 'Safety Violation',
    other: isRTL ? 'أخرى' : 'Other',
    warningAlert: isRTL ? 'تنبيه: الإنذار سيؤثر على سجل الموظف' : 'Alert: Warning will affect employee record',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully'
  };

  useEffect(() => {
    if (open) {
      loadData();
      if (warning) {
        setFormData({
          branchId: warning.branchId,
          employeeId: warning.employeeId,
          warningType: warning.warningType,
          violationType: warning.violationType,
          violationDate: warning.violationDate.split('T')[0],
          issueDate: warning.issueDate.split('T')[0],
          description: warning.description || '',
          actionTaken: warning.actionTaken || '',
          penaltyAmount: warning.penaltyAmount || 0,
          suspensionDays: warning.suspensionDays || 0,
          expiryDate: warning.expiryDate ? warning.expiryDate.split('T')[0] : '',
          notes: warning.notes || ''
        });
      } else {
        resetForm();
      }
    }
  }, [open, warning]);

  useEffect(() => {
    if (formData.branchId) {
      setFilteredEmployees(allEmployees.filter(emp => emp.branchId === formData.branchId));
    } else {
      setFilteredEmployees(allEmployees);
    }
  }, [formData.branchId, allEmployees]);

  useEffect(() => {
    if (formData.issueDate) {
      const issueDate = new Date(formData.issueDate);
      issueDate.setFullYear(issueDate.getFullYear() + 1);
      setFormData(prev => ({ ...prev, expiryDate: issueDate.toISOString().split('T')[0] }));
    }
  }, [formData.issueDate]);

  const loadData = async () => {
    try {
      const [branchesData, employeesData] = await Promise.all([
        branchesService.getAll(),
        employeesService.getAll()
      ]);
      setBranches(branchesData);
      setAllEmployees(employeesData.data || employeesData);
    } catch (error) {
      toast.error('Error loading data');
    }
  };

  const resetForm = () => {
    const issueDate = new Date();
    const expiryDate = new Date(issueDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);

    setFormData({
      branchId: 0,
      employeeId: 0,
      warningType: 'Verbal',
      violationType: 'LateComing',
      violationDate: issueDate.toISOString().split('T')[0],
      issueDate: issueDate.toISOString().split('T')[0],
      description: '',
      actionTaken: '',
      penaltyAmount: 0,
      suspensionDays: 0,
      expiryDate: expiryDate.toISOString().split('T')[0],
      notes: ''
    });
  };

  const handleSave = async () => {
    if (!formData.branchId || !formData.employeeId || !formData.description || !formData.actionTaken) {
      toast.error('Branch, Employee, Description, and Action Taken are required');
      return;
    }

    try {
      setLoading(true);
      if (warning) {
        await warningsService.update({ ...formData, warningId: warning.warningId } as UpdateWarningDto);
      } else {
        await warningsService.create(formData as CreateWarningDto);
      }
      toast.success(t.saveSuccess);
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving');
    } finally {
      setLoading(false);
    }
  };

  const warningTypes = [
    { value: 'Verbal', label: t.verbal },
    { value: 'FirstWritten', label: t.firstWritten },
    { value: 'SecondWritten', label: t.secondWritten },
    { value: 'FinalWritten', label: t.finalWritten },
    { value: 'FinancialPenalty', label: t.financialPenalty },
    { value: 'Suspension', label: t.suspension },
    { value: 'Termination', label: t.termination }
  ];

  const violationTypes = [
    { value: 'LateComing', label: t.lateComing },
    { value: 'Absence', label: t.absence },
    { value: 'PerformanceIssue', label: t.performanceIssue },
    { value: 'Misconduct', label: t.misconduct },
    { value: 'PolicyViolation', label: t.policyViolation },
    { value: 'SafetyViolation', label: t.safetyViolation },
    { value: 'Other', label: t.other }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          {t.title}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>{t.warningAlert}</Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
                value={branches.find(b => b.branchId === formData.branchId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, branchId: newValue?.branchId || 0, employeeId: 0 })}
                renderInput={(params) => <TextField {...params} label={t.branch} required />}
                fullWidth
              />
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={filteredEmployees}
                getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
                value={filteredEmployees.find(e => e.employeeId === formData.employeeId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, employeeId: newValue?.employeeId || 0 })}
                renderInput={(params) => <TextField {...params} label={t.employee} required />}
                filterOptions={filterOptions}
                disabled={!formData.branchId}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField select fullWidth label={t.warningType} value={formData.warningType} onChange={(e) => setFormData({ ...formData, warningType: e.target.value })} required>
                {warningTypes.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField select fullWidth label={t.violationType} value={formData.violationType} onChange={(e) => setFormData({ ...formData, violationType: e.target.value })} required>
                {violationTypes.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.violationDate}
                  value={formData.violationDate ? new Date(formData.violationDate) : null}
                  onChange={(date) => setFormData({ ...formData, violationDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.issueDate}
                  value={formData.issueDate ? new Date(formData.issueDate) : null}
                  onChange={(date) => setFormData({ ...formData, issueDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 30%', minWidth: '150px' }}>
              <TextField fullWidth type="number" label={t.penaltyAmount} value={formData.penaltyAmount} onChange={(e) => setFormData({ ...formData, penaltyAmount: parseFloat(e.target.value) || 0 })} disabled={formData.warningType !== 'FinancialPenalty'} inputProps={{ min: 0, step: 0.001 }} />
            </Box>

            <Box sx={{ flex: '1 1 30%', minWidth: '150px' }}>
              <TextField fullWidth type="number" label={t.suspensionDays} value={formData.suspensionDays} onChange={(e) => setFormData({ ...formData, suspensionDays: parseInt(e.target.value) || 0 })} disabled={formData.warningType !== 'Suspension'} inputProps={{ min: 0 }} />
            </Box>

            <Box sx={{ flex: '1 1 30%', minWidth: '150px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.expiryDate}
                  value={formData.expiryDate ? new Date(formData.expiryDate) : null}
                  onChange={(date) => setFormData({ ...formData, expiryDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box>
            <TextField fullWidth label={t.description} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} required />
          </Box>

          <Box>
            <TextField fullWidth label={t.actionTaken} value={formData.actionTaken} onChange={(e) => setFormData({ ...formData, actionTaken: e.target.value })} multiline rows={2} required />
          </Box>

          <Box>
            <TextField fullWidth label={t.notes} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} multiline rows={2} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" color="error" startIcon={<SaveIcon />} disabled={loading}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarningDialog;